import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import 'moment-timezone';
import {
  Typography,
  Grid,
  IconButton,
  Badge,
  ListItem,
  ListItemAvatar,
  ListItemText,
  useMediaQuery,
} from '@material-ui/core';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import LetteredAvatar from 'react-lettered-avatar';
import PushPinIcon from '@mui/icons-material/PushPin';
import { orange } from '@material-ui/core/colors';
import * as noteActions from '../../../redux/actions/notes';
import { isNotificationUnread } from '../../../helpers/pricing';
import { convertNotesDateToPST } from '../../../helpers/audit-trail';
import { updatePin } from '../../../helpers/api';
import './react-lettered.css';

const styles = makeStyles(theme => ({
  noteCardUser: {
    fontSize: '14px',
  },
  noteCardDate: {
    fontSize: '12px',
  },
  listNoteMd: {
    borderRadius: '5px !important',
    padding: '8px !important',
    paddingTop: '0px !important',
    margin: '0px !important',
    width: 240,
    wordWrap: 'break-word !important',
    overflowWrap: 'break-word !important',
    '& .MuiListItemText-secondary': {
      wordWrap: 'break-word !important',
      overflowWrap: 'break-word !important',
      maxWidth: '100% !important',
    },
  },
  listNote: {
    borderRadius: '5px !important',
    padding: '8px !important',
    paddingTop: '0px !important',
    margin: '0px !important',
    width: 370,
    wordWrap: 'break-word !important',
    overflowWrap: 'break-word !important',
    '& .MuiListItemText-secondary': {
      wordWrap: 'break-word !important',
      overflowWrap: 'break-word !important',
      maxWidth: '100% !important',
    },
  },
  listNoteLg: {
    borderRadius: '5px !important',
    padding: '8px !important',
    paddingTop: '0px !important',
    margin: '0px !important',
    width: 330,
    wordWrap: 'break-word !important',
    overflowWrap: 'break-word !important',
    '& .MuiListItemText-secondary': {
      wordWrap: 'break-word !important',
      overflowWrap: 'break-word !important',
      maxWidth: '100% !important',
    },
  },
  noteDetail: {
    backgroundColor: '#EEEEEE !important',
  },
  noteDetailDark: {
    backgroundColor: '#2D3436  !important',
  },
  pinnedNote: {
    backgroundColor: 'transparent  !important',
  },
  pinnedItem: {
    backgroundColor: '#FEF9EC  !important',
  },
  customBadge: {
    backgroundColor: orange[500],
    height: 12,
    width: 12,
    borderRadius: 10,
  },
  pinnedPin: {
    color: orange[500],
    visibility: 'visible',
    transform: 'rotate3d(1, 6, 18, 50deg)',
    '&:hover': {
      color: orange[500],
      backgroundColor: 'rgba(232, 145, 58, 0.1)',
      transform: 'rotate3d(1, 6, 18, 40deg)',
    },
  },
  unpinnedPin: {
    color: 'gray',
    visibility: 'visible',
    '&:hover': {
      color: '#ab47bc',
      backgroundColor: 'rgba(171, 71, 188, 0.1)',
    },
  },
  hiddenPin: {
    visibility: 'hidden',
  },
  listItem: {
    alignItems: 'flex-end',
    marginBottom: 5,
    width: '96%',
  },
  darkNoteName: {
    color: '#2D3436',
  },
  darkNoteText: {
    color: '#707171',
  },
  accordionSummary: {
    minHeight: '5px !important',
    maxHeight: '5px !important',
    padding: '0px !important',
    paddingLeft: '0px !important',
    paddingRight: '0px !important',
    '&.Mui-expanded': {
      minHeight: '5px !important',
      maxHeight: '5px !important',
    },
    '& .MuiAccordionSummary-content': {
      margin: '0px !important',
      padding: '0px !important',
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      color: '#0a0404ff',
    },
  },
  textContainer: {
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    maxWidth: '100%',
    width: '100%',
    boxSizing: 'border-box',
  },
  textLabel: {
    fontWeight: 700,
    fontSize: '12px',
    lineHeight: '12px',
    letterSpacing: '0.15px',
    whiteSpace: 'nowrap',
  },
  textValue: {
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    fontSize: '12px',
    lineHeight: '12px',
  },
  textValueShort: {
    fontWeight: 700,
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    fontSize: '12px',
    lineHeight: '12px',
  },
  baseContainer: {
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    maxWidth: '100%',
    overflow: 'hidden',
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    width: '274px',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    maxWidth: '100%',
  },
  accordionContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    width: '100%',
    marginLeft: '0px',
    marginRight: '0px',
    boxSizing: 'border-box',
  },
  accordionContainer: {
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    maxWidth: '100%',
    overflow: 'hidden',
    paddingLeft: '0px !important',
    paddingRight: '0px !important',
  },
  tableTitle: {
    wordWrap: 'break-word !important',
    overflowWrap: 'break-word !important',
    fontWeight: 700,
    fontSize: '12px',
    lineHeight: '12px',
    letterSpacing: '0.15px',
    textAlign: 'left',
  },
  tableContainer: {
    boxShadow: 'none !important',
    width: '100%',
  },
  table: {
    borderRadius: '0px !important',
  },
  tableHead: {
    backgroundColor: '#D9DADD !important',
    height: '20px !important',
    '& .MuiTableCell-root': {
      height: '20px !important',
      minHeight: '20px !important',
      maxHeight: '20px !important',
      padding: '2px 8px !important',
      lineHeight: '16px',
      borderRadius: '0px',
      width: '33.33%',
      color: '#7D7D7D',
    },
  },
  tableBody: {
    '& .MuiTableRow-root': {
      '&:last-child td, &:last-child th': {
        border: 0,
      },
      height: '20px !important',
      '& .MuiTableCell-root': {
        height: '20px !important',
        minHeight: '20px !important',
        maxHeight: '20px !important',
        padding: '2px 8px !important',
        lineHeight: '16px',
        backgroundColor: '#EEEEEE',
        border: '0px',
        fontWeight: 700,
        color: theme.palette.type === 'dark' ? '#A0A0A0 !important' : '#3C3C3C',
      },
    },
  },
  tableCell: {
    border: '0px !important',
  },
  accordionDivider: {
    width: '100%',
    border: '0.5px solid #ccc',
    margin: 0,
  },
  accordion: {
    maxWidth: '100% !important',
    boxShadow: 'none !important',
    paddingLeft: '0px !important',
    paddingRight: '0px !important',
    wordWrap: 'break-word !important',
    overflowWrap: 'break-word !important',
    overflow: 'hidden !important',
    '&:before': {
      display: 'none !important',
    },
  },
  accordionPinned: {
    backgroundColor: '#FEF9EC !important',
  },
  accordionUnpinned: {
    backgroundColor: theme.palette.type === 'dark' ? '#2D3436 !important' : '#EEEEEE !important',
  },
  pmDropsContainer: {
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    maxWidth: '100%',
    width: '100%',
    boxSizing: 'border-box',
  },
  pmDropsLabel: {
    fontWeight: 700,
    fontSize: '12px',
    lineHeight: '12px',
    letterSpacing: '0.15px',
  },
  pmDropsValue: {
    fontSize: '12px',
    lineHeight: '12px',
    fontWeight: 700,
  },
}));

const Note = props => {
  const theme = useTheme();
  const { note, newNotes, handleNoteClick, handlePin } = props;
  const noteDate = convertNotesDateToPST(note.created_at).replace(',', '');
  const [isUnread, setUnread] = useState(false);
  const [isPinned, setPinned] = useState(false);
  const [inFocus, setFocus] = useState(false);
  const pageMD = useMediaQuery(theme.breakpoints.only('md'));
  const pageLG = useMediaQuery(theme.breakpoints.only('lg'));
  const pageXL = useMediaQuery(theme.breakpoints.only('xl'));

  const classes = styles();
  const getUserStringBase = () => {
    const { user_id: userID, display_name: displayName, user_email: userEmail } = note;
    let userText = userID;
    if (typeof displayName !== 'undefined' && displayName.length) userText = displayName;
    else if (typeof userEmail !== 'undefined' && userEmail.length) userText = userEmail;

    let words = userText.split(' ');
    if (words && words.length > 0) {
      words = words.filter(word => word.length > 0);
      return words
        .map(word => {
          return word[0].toUpperCase() + word.substring(1);
        })
        .join(' ');
    }

    return userText;
  };

  useEffect(() => {
    if (newNotes) {
      const unread = isNotificationUnread(note, newNotes);
      setUnread(unread);
    }
  }, [newNotes]);

  function returnNoteContent(theNote) {
    const getTextColor = elementType => {
      if (theme.palette.type === 'light') {
        if (elementType === 'label') {
          return '#676767';
        }
        if (elementType === 'value') {
          return note.pinned ? '#A0A0A0' : '#3C3C3C';
        }
      }
      if (elementType === 'label') {
        return note.pinned ? '#676767' : '#FFFFFF';
      }

      if (elementType === 'value') {
        return note.pinned ? '#A0A0A0' : '#E0E0E0';
      }

      return '#FFFFFF';
    };

    const createTextComponent = (label, value, valueClass = 'textValue') => (
      <div className={classes.textContainer}>
        <span className={classes.textLabel} style={{ color: getTextColor('label') }}>
          {label}
        </span>
        <span className={classes[valueClass]} style={{ color: getTextColor('value') }}>
          {value}
        </span>
      </div>
    );

    const currentPeriodText = theNote?.endpoint ? (
      <div className={classes.textContainer}>
        <div className={classes.textLabel} style={{ color: getTextColor('label') }}>
          Current Period:
        </div>
        <div className={classes.textValueShort} style={{ color: getTextColor('value'), marginTop: '5px' }}>
          {`${theNote?.current_period} - ${theNote?.endpoint}% Endpoint - ${Math.round(
            theNote?.sold_percentage
          )}% Sold`}
        </div>
      </div>
    ) : (
      createTextComponent(
        'Current Period: ',
        `${theNote?.current_period} - ${Math.round(theNote?.sold_percentage)}% Sold`,
        'textValueShort'
      )
    );

    const actionText = createTextComponent('Action: ', `${theNote?.action}`, 'textValueShort');

    const pricingText = createTextComponent('Pricing Strategy: ', `${theNote?.pricing_strategy}`);

    const ceilingText = createTextComponent('Ceiling: ', `${theNote?.ceiling}`);

    const bidPriceDelta = createTextComponent('Bid Price Delta: ', `$${theNote?.bid_price_delta}`, 'textValueShort');

    const kobeSelloutDTE = createTextComponent('Kobe Sellout DTE: ', `${theNote?.kobe_sellout_dte}`, 'textValueShort');

    const primaryQuantityStatus = createTextComponent(
      'Primary Quantity Status: ',
      `${theNote?.primary_quantity_status}`,
      'textValueShort'
    );

    const pmDropsCuts = (
      <div className={classes.pmDropsContainer}>
        <span className={classes.pmDropsLabel} style={{ color: getTextColor('label') }}>
          PM Drops/Cuts:
        </span>
        <span className={classes.pmDropsValue} style={{ color: getTextColor('value') }}>
          {` ${theNote?.pm_drops_cuts}`}
        </span>
        <span className={classes.pmDropsLabel} style={{ color: getTextColor('label') }}>
          - Get-In
        </span>
        <span className={classes.pmDropsValue} style={{ color: getTextColor('value') }}>
          {` $${theNote?.get_in_price}`}
        </span>
      </div>
    );

    const dataIQRunningSales = createTextComponent(
      'Data IQ Running Sales: ',
      `${theNote?.data_iq_sales}`,
      'textValueShort'
    );

    return (
      <div className={classes.baseContainer}>
        <div className={classes.flexColumn}>
          <div>{currentPeriodText}</div>
          {theNote?.action && <div>{actionText}</div>}
          {theNote?.pricing_strategy && <div>{pricingText}</div>}
          {theNote?.ceiling && (
            <div>
              <div>{ceilingText}</div>
            </div>
          )}
        </div>
        <Accordion
          className={`${classes.accordion} ${note.pinned ? classes.accordionPinned : classes.accordionUnpinned}`}
        >
          <AccordionSummary
            expandIcon={<ArrowDropDownIcon />}
            aria-controls="panel2-content"
            id="panel2-header"
            className={classes.accordionSummary}
          >
            <hr className={classes.accordionDivider} />
          </AccordionSummary>
          <AccordionDetails className={classes.accordionContainer}>
            <div className={classes.accordionContent}>
              {theNote?.primary_quantity_status && <div>{primaryQuantityStatus}</div>}
              <div>{pmDropsCuts}</div>
              {theNote?.kobe_sellout_dte && <div>{kobeSelloutDTE}</div>}
              {theNote?.bid_price_delta && <div>{bidPriceDelta}</div>}
              {theNote?.data_iq_sales && <div className={classes.textContainer}>{dataIQRunningSales}</div>}
              {theNote.data_iq?.length > 0 && (
                <>
                  <div className={classes.tableTitle} style={{ color: getTextColor('label') }}>
                    Data IQ Sales and Avg. Sales Price Per Zone
                  </div>
                  <div>
                    <TableContainer component={Paper} className={classes.tableContainer}>
                      <Table aria-label="simple table" className={classes.table}>
                        <TableHead>
                          <TableRow className={classes.tableHead}>
                            <TableCell>Zone</TableCell>
                            <TableCell>Avg. Price</TableCell>
                            <TableCell>Total Sales</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody className={classes.tableBody}>
                          {theNote.data_iq.map(row => (
                            <TableRow key={row.name}>
                              <TableCell className={classes.tableCell}>{row.zone}</TableCell>
                              <TableCell className={classes.tableCell}>{`$${row.avg_price}`}</TableCell>
                              <TableCell className={classes.tableCell}>{row.total_sales}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </>
              )}
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    );
  }

  useEffect(() => {
    if (note.pinned) {
      setPinned(true);
    } else {
      setPinned(false);
    }
  }, [note]);

  const handlePinned = () => {
    const pinned = !isPinned;
    setPinned(pinned);
    updatePin({
      entityID: note.entity,
      userVersion: note.user_version,
      pinned,
    });
    handlePin(note, pinned);
  };

  const handleFocus = (event, focus) => {
    setFocus(focus);
  };

  const noteHeader = (
    <Grid container direction="row" alignItems="baseline" justifyContent="space-between">
      <Grid item>
        <Grid container direction={pageMD ? 'column' : 'row'} alignItems="baseline" spacing={pageMD ? 0 : 1}>
          <Grid item>
            <Typography className={theme.palette.type === 'dark' && isPinned ? classes.darkNoteName : ''}>
              <span className={classes.noteCardUser}>{getUserStringBase()}</span>
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              className={`${classes.noteCardDate} ${
                theme.palette.type === 'dark' && isPinned ? classes.darkNoteText : ''
              }`}
            >
              {noteDate.includes('PM') ? noteDate.replace('PM', 'P.M.') : noteDate.replace('AM', 'A.M.')}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <IconButton
          aria-label="pin"
          /* eslint-disable-next-line no-nested-ternary */
          className={isPinned ? classes.pinnedPin : inFocus ? classes.unpinnedPin : classes.hiddenPin}
          onClick={handlePinned}
        >
          <PushPinIcon />
        </IconButton>
      </Grid>
    </Grid>
  );

  return (
    <ListItem
      onMouseEnter={event => handleFocus(event, true)}
      onMouseLeave={event => handleFocus(event, false)}
      onClick={() => handleNoteClick(note)}
      className={`${classes.listItem} ${isPinned ? classes.pinnedItem : ''}`}
    >
      <ListItemAvatar style={{ minWidth: 40 }}>
        <LetteredAvatar size={35} style={{ fontSize: '16px', color: 'white' }} name={getUserStringBase()} />
      </ListItemAvatar>
      <Badge variant="dot" size="large" classes={{ badge: classes.customBadge }} invisible={!isUnread}>
        <ListItemText
          primary={noteHeader}
          secondary={returnNoteContent(note)}
          className={`${
            /* eslint-disable-next-line no-nested-ternary */
            pageXL ? classes.listNote : pageLG ? classes.listNoteLg : classes.listNoteMd
          }
          ${
            /* eslint-disable-next-line no-nested-ternary */
            isPinned ? classes.pinnedNote : theme.palette.type === 'light' ? classes.noteDetail : classes.noteDetailDark
          }`}
          secondaryTypographyProps={{
            style: {
              marginTop: pageMD ? 2 : -10,
              color:
                /* eslint-disable-next-line no-nested-ternary */
                theme.palette.type === 'dark' && isPinned
                  ? '#707171'
                  : theme.palette.type === 'dark'
                  ? 'white'
                  : 'black',
            },
          }}
        />
      </Badge>
    </ListItem>
  );
};
Note.propTypes = {
  handleNoteClick: PropTypes.func.isRequired,
  handlePin: PropTypes.func.isRequired,
  newNotes: PropTypes.array.isRequired,
  note: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  noteEditError: state.notes.ui.noteEditError,
});

const mapDispatchToProps = dispatch => ({ eventActions: bindActionCreators(noteActions, dispatch) });

export default connect(mapStateToProps, mapDispatchToProps)(Note);
